import type { DOMConversionMap, EditorConfig, ElementFormatType, NodeKey, DOMConversionOutput } from 'lexical';
import { $applyNodeReplacement, setNodeIndentFromDOM } from "lexical";
import type { SerializedQuoteNode } from '@lexical/rich-text';
import { QuoteNode } from "@lexical/rich-text";

export type SerializedCustomQuoteNode = SerializedQuoteNode & {
	classes: string;
};

export class CustomQuoteNode extends QuoteNode {
	__classes: string;

	static getType(): string {
		return "custom-quote";
	}

	static clone(node: CustomQuoteNode) {
		return new CustomQuoteNode(node.__classes, node.__key);
	}

	constructor(classes: string = '', key?: NodeKey) {
		super(key);
		this.__classes = classes;
	}

	getClasses(): string {
		return this.__classes;
	}

	setClasses(classes: string) {
		const writable = super.getWritable();
		writable.__classes = classes;
	}

	createDOM(config: EditorConfig): HTMLElement {
		const dom = super.createDOM(config);
		dom.className = this.getClasses();
		return dom;
	}

	updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
		const classes = this.__classes;

		if (classes !== prevNode.__classes) {
			dom.className = classes;
		}

		return false;
	}

	static importDOM(): DOMConversionMap {
		return {
			blockquote: (node: Node) => ({
				conversion: $convertQuoteElement,
				priority: 1,
			}),
		};
	}

	static importJSON(serializedNode: SerializedCustomQuoteNode): CustomQuoteNode {
		const node = $createCustomQuoteNode().updateFromJSON(serializedNode);
		node.setClasses(serializedNode.classes);
		return node;
	}

	exportJSON(): SerializedCustomQuoteNode {
		return {
			...super.exportJSON(),
			type: 'custom-quote',
			classes: this.getClasses(),
		};
	}
}

export function $createCustomQuoteNode(): CustomQuoteNode {
	return $applyNodeReplacement(new CustomQuoteNode());
}

export function $isCustomQuoteNode(node: any) {
	return node instanceof CustomQuoteNode;
}

function $convertQuoteElement(element: HTMLElement): DOMConversionOutput {
	const node = $createCustomQuoteNode();

	if (element.style) {
		node.setFormat(element.style.textAlign as ElementFormatType);
		setNodeIndentFromDOM(element, node);
	}

	if (element.className) {
		node.setClasses(element.className);
	}

	return {node};
}
